import { isMockMode, supabase } from './supabaseClient';

const META_KEY = 'pcnguard_fleet_prefs';
const LS_PREFIX = 'pcnguard_fleet:prefs:';

const safeParse = (value: string | null) => {
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const getLocalStorageKey = async (key: string) => {
  if (isMockMode) return `${LS_PREFIX}mock-user:${key}`;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return `${LS_PREFIX}${user?.id || 'anonymous'}:${key}`;
  } catch {
    return `${LS_PREFIX}anonymous:${key}`;
  }
};

export const UserPreferences = {
  async get<T>(key: string, fallback: T): Promise<T> {
    // 1) Try Supabase user_metadata (persists across devices)
    if (!isMockMode) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const prefs = (user?.user_metadata as any)?.[META_KEY] as Record<string, unknown> | undefined;
        if (prefs && Object.prototype.hasOwnProperty.call(prefs, key)) {
          return prefs[key] as T;
        }
      } catch {
        // Fall back to localStorage
      }
    }

    // 2) Fall back to localStorage (works in demo/mock mode)
    try {
      const storageKey = await getLocalStorageKey(key);
      const parsed = safeParse(localStorage.getItem(storageKey));
      return (parsed === undefined ? fallback : (parsed as T));
    } catch {
      return fallback;
    }
  },

  async set<T>(key: string, value: T): Promise<void> {
    // Always write localStorage (instant UI + works offline/mock)
    try {
      const storageKey = await getLocalStorageKey(key);
      localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // ignore
    }

    // Best-effort write to Supabase metadata (persists across devices)
    if (!isMockMode) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const existing = ((user.user_metadata as any)?.[META_KEY] || {}) as Record<string, unknown>;
        const next = { ...existing, [key]: value };
        await supabase.auth.updateUser({ data: { [META_KEY]: next } as any });
      } catch {
        // ignore
      }
    }
  }
};

